/**
 * @file The live part list of a running Signify fetch, one row per input in operator order.
 * Consumer: `SignifyFetchJobProgress`.
 * It subscribes to `loading` and `docIds` and to nothing else. A part transition is a same-position
 * `modified` change, which never touches `docIds`, so the memoized `dataSource` keeps its identity,
 * antd's `Table` receives identical props and does not render — each column's `render` mounts a cell
 * that subscribes to its own part instead. Reading `docs` here would put all thousand part writes
 * through the virtual list's layout pass, which is the SYD-2264 shape of regression.
 * `virtual` requires numeric `scroll.x` and `scroll.y` and a `width` on every column, and forbids a
 * `components.body.row` override; the widths below sum to `TABLE_WIDTH`.
 */

import { FC, useMemo } from 'react';
import { Table, Typography, type TableProps } from 'antd';
import { useSnapshot } from 'valtio';
import type { SignifyFetchPart } from '#types';
import type { CollectionStore } from '@/store';
import SignifyFetchJobPartCell from './SignifyFetchJobPartCell';
import SignifyFetchJobPartDetailCell from './SignifyFetchJobPartDetailCell';
import SignifyFetchJobPartLeadTimeCell from './SignifyFetchJobPartLeadTimeCell';
import SignifyFetchJobPartPriceCell from './SignifyFetchJobPartPriceCell';
import SignifyFetchJobPartStatusCell from './SignifyFetchJobPartStatusCell';
import type { SignifyFetchJobPicks } from '../index.types';

const { Text } = Typography;

const TABLE_WIDTH = 940;
const TABLE_HEIGHT = 400;

type SignifyFetchJobPartRow = { key: string };

type SignifyFetchJobPartTableProps = {
  partsStore: CollectionStore<SignifyFetchPart>;
  picks: SignifyFetchJobPicks;
  retryDisabledReason: string | null;
  onRetryPart: (partId: string) => void;
};

const SignifyFetchJobPartTable: FC<SignifyFetchJobPartTableProps> = ({
  partsStore,
  picks,
  retryDisabledReason,
  onRetryPart,
}) => {
  const snap = useSnapshot(partsStore.state);
  const { docIds, loading } = snap;

  const dataSource = useMemo<SignifyFetchJobPartRow[]>(
    () => docIds.map((key) => ({ key })),
    [docIds],
  );

  const columns = useMemo<TableProps<SignifyFetchJobPartRow>['columns']>(() => [
    {
      title: '#',
      key: 'position',
      width: 50,
      render: (_value, _record, index) => <Text type="secondary">{index + 1}</Text>,
    },
    {
      title: 'Status',
      key: 'status',
      width: 120,
      render: (_value, record) => {
        const entry = partsStore.state.docs.get(record.key);
        return entry ? <SignifyFetchJobPartStatusCell part={entry.data} /> : null;
      },
    },
    {
      title: 'Part',
      key: 'part',
      width: 300,
      render: (_value, record) => {
        const entry = partsStore.state.docs.get(record.key);
        return entry ? <SignifyFetchJobPartCell part={entry.data} /> : null;
      },
    },
    {
      title: 'Price',
      key: 'price',
      width: 120,
      render: (_value, record) => {
        const entry = partsStore.state.docs.get(record.key);
        return entry ? <SignifyFetchJobPartPriceCell part={entry.data} /> : null;
      },
    },
    {
      title: 'Lead time',
      key: 'leadTime',
      width: 120,
      render: (_value, record) => {
        const entry = partsStore.state.docs.get(record.key);
        return entry ? <SignifyFetchJobPartLeadTimeCell part={entry.data} /> : null;
      },
    },
    {
      title: 'Detail',
      key: 'detail',
      width: 230,
      render: (_value, record) => {
        const entry = partsStore.state.docs.get(record.key);
        return entry ? (
          <SignifyFetchJobPartDetailCell
            part={entry.data}
            picks={picks}
            retryDisabledReason={retryDisabledReason}
            onRetryPart={onRetryPart}
          />
        ) : null;
      },
    },
  ], [onRetryPart, partsStore, picks, retryDisabledReason]);

  return (
    <Table<SignifyFetchJobPartRow>
      virtual
      aria-label="signify-fetch-job-part-table"
      size="small"
      rowKey="key"
      columns={columns}
      dataSource={dataSource}
      loading={loading}
      pagination={false}
      scroll={{ x: TABLE_WIDTH, y: TABLE_HEIGHT }}
      locale={{ emptyText: <Text type="secondary">Parts appear here as the fetch starts</Text> }}
    />
  );
};

export default SignifyFetchJobPartTable;
